import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { SubjectType } from 'app/shared/models/subject-type';
import { Subscription } from 'rxjs';
import { CountWidgetSettings } from 'app/shared/models/count-widget-settings';
import { TaskStatusService } from 'app/shared/services/task-type.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { Organisation } from 'app/shared/models/organisations';
import { Utilities } from 'app/shared/helpers/utilities';
import { LabelService } from '../../label.service';
import { UserService } from 'app/shared/services/user.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'task-count-widget-settings',
  templateUrl: './task-count-widget-settings.component.html',
  styleUrls: ['./task-count-widget-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TaskCountWidgetSettingsComponent implements OnInit, OnDestroy {

  onOrganisationsChanged: Subscription;
  activeUsers: Subscription;
  valueChanges: Subscription;

  form: UntypedFormGroup;
  settings: CountWidgetSettings;

  contexts = this.labelService.CONTEXTS.filter(i => i.id != -2);
  contextGeneral = this.labelService.CONTEXTS.filter(i => i.id != -2);
  contextUsers: any[] = [];
  prevContextValue: any;

  types: SubjectType[] = [];
  states: string[];
  organisations: Organisation[] = [];
  displayExtra = false;
  color: string;
  originalColor: string;
  organisationLabel = 'Organisation';
  showOrganisations = false
  organisaitonReadonly: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<TaskCountWidgetSettingsComponent>,
    private taskStatusService: TaskStatusService, 
    private organisationService: OrganisationService,
    private labelService: LabelService,
    private userService: UserService,
    @Inject(MAT_DIALOG_DATA) public data: CountWidgetSettings,
    private fb: UntypedFormBuilder) {
      this.settings = data || new CountWidgetSettings();
      this.settings.color = this.settings.color || '#f44336'; // 'rgb(244, 67, 54)';
    }

  ngOnInit() {
    const taskstatuses = this.taskStatusService.taskStatusTypeList.map((status) => status.name);
    
    // check if due has been included in the taskstatuses, if not add it as we need it for the stats...
    if (taskstatuses.indexOf('Due') === -1){
      taskstatuses.splice(1, 0, 'Due');
    }

    // check if created has been included in the taskstatuses, if not add it as we need it for the stats...
    if (taskstatuses.indexOf('Created') === -1){
      taskstatuses.unshift('Created');
    }   

    // tslint:disable-next-line:only-arrow-functions
    taskstatuses.sort(function(a, b){return a.localeCompare(b); });

    // add an 'All' option
    taskstatuses.unshift('All');
    
    this.states = taskstatuses;

    this.activeUsers = this.userService.getActiveUsers().subscribe((users) => {
      const meId = this.userService.getCurrentUserId();
      this.contextUsers = [];
      users.forEach((i) => {
        if ((i.id === meId) || (i.managerId === meId)) {
          this.contextUsers.push({ id: i.id, name: (i.id === meId) ? 'Me' : i.fullName });
        }
      });
      this.contextUsers.sort((a, b) => a.name.localeCompare(b.name)).forEach((i) => {
        this.contexts.push(i);
      });
    });

    this.showOrganisations = false; // this.organisationService.showOrganisations;
    if (this.userService.getCurrentUser().role.toLocaleLowerCase()=='multiorgadmin' || this.userService.getCurrentUser().role.toLocaleLowerCase()=='admin'){
      this.showOrganisations = this.organisationService.showOrganisations;
    }

    if (this.showOrganisations){
      this.onOrganisationsChanged = this.organisationService.onChanged.subscribe((response) => {
        if (response.length > 0){
          // const organisations = response.filter((organisation) => ((organisation.hideRecord !== true) || 
          // (this.data && ((organisation.id === this.data.organisationId)))));
          // organisations.sort((a, b) => a.name.localeCompare(b.name));
          // organisations.unshift({id : -1, name: 'All'});
          // this.organisations = organisations;

          var organisations = response;
          if (this.userService.hasPriv('multiorgadmin')) {
            var userOrgs = this.userService.getCurrentUser()?.userOrganisations;
            if (environment.limitAccessToSubjectsByOrg && userOrgs && userOrgs.length > 0 && this.userService.getCurrentUser()) {
              organisations = organisations.filter(i => userOrgs.findIndex(j => j.organisationId == i.id) > -1);
            }
          }
          else if (environment.limitAccessToSubjectsByOrg && this.userService.getCurrentUser().role.toLocaleLowerCase() !='admin') {
            organisations = organisations.filter(i => i.id == this.userService.getCurrentUser()?.organisationId);
          }
  
          if (organisations.length > 1) {
            organisations.sort((a, b) => a.name.localeCompare(b.name));
            organisations = organisations.filter(i=>{ return (i.id!=-1)});
            organisations.unshift({ id: -1, name: 'All' });
          }
          this.organisations = organisations;
        }
      });

      this.organisationService.getAll();
      this.organisationLabel = this.organisationService.organisationLabel();
    }

    this.prevContextValue = this.settings.context;
    if (this.settings.context) {
      this.prevContextValue = this.labelService.getContext(this.settings.context);
    }

    this.form = this.fb.group({
      context: [this.prevContextValue, Validators.required],
      state: [this.data.state || 'All', Validators.required],
      // color: [this.data.color],
      organisationId: [this.data.organisationId || -1, Validators.required],
      showExtra: [this.data.showExtra],
      extraState: [this.data.extraState || 'All'],
    });

    // set colour
    this.color = this.data.color;
    this.originalColor = this.data.color;

    this.formControlValueChanged();

    Utilities.patchCheckboxAria();
  }

  ngOnDestroy(){
    this.onOrganisationsChanged?.unsubscribe();
    this.activeUsers?.unsubscribe();
    this.valueChanges?.unsubscribe();
  }

  formControlValueChanged(){
    const extraStateControl = this.form.get('extraState');
    const showExtraControl = this.form.get('showExtra');

    if (extraStateControl && showExtraControl) {
      this.valueChanges = showExtraControl.valueChanges.subscribe((val) => {
        if (val){
          extraStateControl.setValidators([Validators.required]);
          // MRD - delay the hiding of the control just long enough for the validation change to take effect
          this.displayExtra = true;
          setTimeout(() => { this.displayExtra = false; }, 100);
        }
        else{
          extraStateControl.clearValidators();
        }
        extraStateControl.updateValueAndValidity();
      });
    }
  }

  contextChange(ev: MatSelectChange) {
    const val = ev.value;
    if (val && Array.isArray(val)) {
      const newValues = (this.prevContextValue && (this.prevContextValue.length > 0)) ? val.filter((i) => !this.prevContextValue.includes(i)) : val;
      const cValues = newValues.filter((i) => i < 1);
      const patchValue = (cValues.length > 0) ? [cValues[0]] : val.filter((i) => i > 0);
      this.form.controls.context.patchValue(patchValue);
      this.prevContextValue = patchValue;
    } else {
      this.prevContextValue = ev.value;
    }
  }

  onColorChange(color){
    if (color && (this.originalColor !== color)){
        this.form.markAsDirty();
    }
  } 

  save(){
    const data = this.form.getRawValue();
    data.color = this.color;
    this.dialogRef.close({ data });
  }
}
